import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section } from "../components/site/blocks";
import { Field, inputProps, Honeypot, Consent, CONSENT_MSG } from "../components/site/FormField";
import { api, formatApiError, EMAIL_RE, PHONE_RE, CP_RE } from "../lib/api";

const REASONS = ["Diagnostic autonomie / accessibilité", "Diagnostic d'éligibilité aux aides", "Rénovation énergétique", "Projet professionnel (AMO)", "Autre"];

const SlotPicker = ({ slots, selected, onSelect }) => {
  const days = useMemo(() => {
    const m = new Map();
    slots.forEach((s) => { if (!m.has(s.date)) m.set(s.date, { label: s.date_label, items: [] }); m.get(s.date).items.push(s); });
    return [...m.entries()];
  }, [slots]);
  const [day, setDay] = useState(null);
  useEffect(() => { if (days.length && !days.find(([d]) => d === day)) setDay(days[0][0]); }, [days, day]);
  if (!days.length) return <p className="card-soft" data-testid="no-slots">Aucun créneau n'est disponible pour le moment. <Link to="/contact?type=rappel" className="link-u text-navy font-semibold">Demandez à être rappelé</Link>.</p>;
  const current = days.find(([d]) => d === day);
  return (
    <div data-testid="rdv-booking-calendar">
      <h2 className="text-2xl">1. Choisissez une date</h2>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" role="listbox" aria-label="Dates disponibles">
        {days.map(([d, v]) => {
          const [wd, n, mo] = v.label.split(" ");
          return (
            <button key={d} type="button" role="option" aria-selected={d === day} onClick={() => setDay(d)} data-testid={`rdv-day-${d}`}
              className={`shrink-0 w-[92px] rounded-2xl border-2 py-3 text-center transition-colors ${d === day ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-navy hover:border-navy/50"}`}>
              <span className="block text-sm capitalize">{wd.slice(0, 3)}.</span>
              <span className="block font-display text-3xl leading-tight">{n}</span>
              <span className="block text-sm">{mo.slice(0, 4)}.</span>
            </button>
          );
        })}
      </div>
      {current && (
        <>
          <h2 className="mt-6 text-2xl">2. Choisissez un horaire <span className="text-lg font-sans font-normal text-slate-600 capitalize">— {current[1].label}</span></h2>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {current[1].items.map((s) => (
              <button key={s.id} type="button" onClick={() => onSelect(s)} aria-pressed={selected?.id === s.id} data-testid={`rdv-slot-${s.id}`}
                className={`rounded-xl border-2 py-3 text-lg font-semibold inline-flex items-center justify-center gap-2 transition-colors ${selected?.id === s.id ? "border-brand bg-brand-ink text-white" : "border-slate-200 bg-white text-navy hover:border-brand/60"}`}>
                <Clock className="h-4 w-4" aria-hidden="true" />{s.time.replace(":", " h ")}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function RendezVous() {
  useSeo("Prendre rendez-vous pour un diagnostic", "Réservez en ligne un créneau de diagnostic avec un conseiller Habitalys : adaptation, sécurisation, éligibilité aux aides, rénovation énergétique.");
  const [slots, setSlots] = useState(null);
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ profile: "particulier", name: "", email: "", phone: "", address: "", postal_code: "", reason: REASONS[0], message: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const load = useCallback(() => api.get("/slots").then((r) => setSlots(r.data)).catch(() => setSlots([])), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!selected) e.slot = "Merci de choisir un créneau.";
    if (f.name.trim().length < 2) e.name = "Merci d'indiquer votre nom.";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Adresse email invalide.";
    if (f.phone.trim() && !PHONE_RE.test(f.phone.trim())) e.phone = "Numéro invalide (exemple : 06 12 34 56 78).";
    if (f.address.trim().length < 4) e.address = "Merci d'indiquer l'adresse du logement.";
    if (!CP_RE.test(f.postal_code.trim())) e.postal_code = "Code postal à 5 chiffres requis.";
    if (!f.consent) e.consent = CONSENT_MSG;
    setErrors(e); setServerError("");
    if (Object.keys(e).length) return;
    setState("loading");
    try {
      const r = await api.post("/appointments", { ...f, phone: f.phone.trim() || null, slot_id: selected.id });
      setConfirmation(r.data); setState("done");
    } catch (err) {
      setServerError(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 409) { setSelected(null); load(); }
      setState("idle");
    }
  };

  if (state === "done" && confirmation) return (
    <Section className="bg-mist" testid="rdv-confirmation">
      <div className="max-w-2xl rounded-[28px] bg-white p-8 sm:p-12 border border-navy/10">
        <CheckCircle2 className="h-12 w-12 text-green-700" aria-hidden="true" />
        <h1 className="mt-5 text-3xl sm:text-4xl">Votre rendez-vous est confirmé</h1>
        <p className="mt-4 text-xl text-navy" data-testid="rdv-confirmation-datetime">Le <strong className="capitalize">{confirmation.date_label}</strong> à <strong>{confirmation.time.replace(":", " h ")}</strong> (heure de Paris)</p>
        <p className="mt-4 text-slate-700">Un email de confirmation vous est envoyé. Un conseiller vous contactera avant le rendez-vous pour en préciser les modalités.</p>
        <Link to="/" className="btn-primary mt-8">Retour à l'accueil</Link>
      </div>
    </Section>
  );

  return (
    <Section className="bg-mist-50" testid="rdv-page">
      <div className="max-w-3xl">
        <p className="eyebrow"><CalendarCheck className="h-4 w-4" aria-hidden="true" /> Prendre RDV</p>
        <h1 className="mt-4 text-4xl sm:text-5xl leading-tight">Réservez votre diagnostic</h1>
        <p className="mt-5 text-lg text-slate-700">Choisissez un créneau disponible : un conseiller Habitalys échange avec vous et organise la visite du logement. Les créneaux sont du lundi au vendredi, à l'heure de Paris.</p>
      </div>
      <form noValidate onSubmit={submit} className="relative mt-10 grid lg:grid-cols-12 gap-10" data-testid="rdv-form">
        <div className="lg:col-span-7 rounded-[28px] bg-white border border-navy/10 p-6 sm:p-8">
          {slots === null ? <p className="text-slate-600 flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Chargement des créneaux…</p>
            : <SlotPicker slots={slots} selected={selected} onSelect={setSelected} />}
          {errors.slot && <p role="alert" data-testid="error-slot" className="mt-4 text-red-700 font-medium">{errors.slot}</p>}
          {selected && <p className="mt-6 rounded-xl bg-mist p-4 text-navy font-semibold" data-testid="rdv-selected-summary">Créneau choisi : <span className="capitalize">{selected.date_label}</span> à {selected.time.replace(":", " h ")}</p>}
        </div>
        <div className="lg:col-span-5 rounded-[28px] bg-white border border-navy/10 p-6 sm:p-8 space-y-5">
          <h2 className="text-2xl">3. Vos coordonnées</h2>
          <Field id="profile" label="Vous êtes">
            <select {...inputProps("profile", errors)} value={f.profile} onChange={set("profile")}>
              <option value="particulier">Particulier</option><option value="professionnel">Professionnel</option>
            </select>
          </Field>
          <Field id="name" label="Nom et prénom" required error={errors.name}><input {...inputProps("name", errors)} value={f.name} onChange={set("name")} autoComplete="name" /></Field>
          <Field id="email" label="Email" required error={errors.email}><input {...inputProps("email", errors)} type="email" value={f.email} onChange={set("email")} autoComplete="email" /></Field>
          <Field id="phone" label="Téléphone (facultatif)" error={errors.phone}><input {...inputProps("phone", errors)} type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" /></Field>
          <Field id="address" label="Adresse du logement" required error={errors.address}><input {...inputProps("address", errors)} value={f.address} onChange={set("address")} autoComplete="street-address" /></Field>
          <Field id="postal_code" label="Code postal" required error={errors.postal_code}><input {...inputProps("postal_code", errors)} inputMode="numeric" maxLength={5} value={f.postal_code} onChange={set("postal_code")} autoComplete="postal-code" /></Field>
          <Field id="reason" label="Motif">
            <select {...inputProps("reason", errors)} value={f.reason} onChange={set("reason")}>{REASONS.map((r) => <option key={r}>{r}</option>)}</select>
          </Field>
          <Field id="message" label="Précisions (facultatif)"><textarea {...inputProps("message", errors)} rows={3} value={f.message} onChange={set("message")} /></Field>
          <Honeypot value={f.website} onChange={set("website")} />
          <Consent checked={f.consent} onChange={set("consent")} error={errors.consent} />
          {serverError && <p role="alert" data-testid="rdv-server-error" className="rounded-xl bg-red-50 text-red-800 p-4 font-medium">{serverError}</p>}
          <button type="submit" className="btn-accent w-full text-lg py-4" disabled={state === "loading"} data-testid="rdv-submit-button">
            {state === "loading" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Confirmer le rendez-vous
          </button>
        </div>
      </form>
    </Section>
  );
}
